import UserAvatar from "components/ui/v2/user-avatar-compat-v2";
import { useUserStoreContext } from "context/userStore/userStore.provider";
import useTranslation from "next-translate/useTranslation";
import { ReactNode } from "react";
import { UserTooltip } from "components/ui/v2";
import { IUserParticipation } from "types/Question/DailyQuestionStats";
import { IUser } from "types/User.types";

interface ISimpUserBadge {
  size?: number;
  user: IUser | IUserParticipation;
  fontSize?: number;
  showEmail?: boolean;
  previewProfile?: boolean;
  customElement?: ReactNode;
  showAvatar?: boolean;
  icon?: string | ReactNode;
  showRole?: boolean;
  overrideRoleName?: string;
  showName?: boolean;
  titleClassname?: string;
  iconClassName?: string;
}

const SimpUserBadge = ({
  user,
  size = 40,
  fontSize = 11,
  showEmail = true,
  previewProfile = false,
  customElement,
  showAvatar = true,
  icon = null,
  showRole = false,
  overrideRoleName = "",
  showName = true,
  titleClassname = "",
  iconClassName = "",
}: ISimpUserBadge) => {
  const { user: storeSelectedVendor } = useUserStoreContext((store) => ({
    ...store,
  }));

  const { t } = useTranslation("common");

  if (!user)
    return <span className="text-xs">{t("QUESTION_WITHOUT_CREATOR")}</span>;

  const Content = () => (
    <div className="flex w-max flex-col">
      <span
        className={`self-start truncate font-semibold capitalize text-black dark:text-white ${titleClassname}`}
        style={{
          fontSize,
          wordBreak: "break-word",
        }}
      >
        {showName && fullName?.toLowerCase()}
      </span>{" "}
      {showEmail && (
        <span
          className="flex overflow-hidden text-gray-800 dark:text-gray-200"
          style={{
            fontSize,
            textOverflow: "ellipsis",
          }}
        >
          {user?.email || "-"}
        </span>
      )}
      {showRole && (
        <span
          className="self-start overflow-hidden text-xs capitalize text-gray-500 dark:text-gray-300"
          style={{ fontSize }}
        >
          {overrideRoleName || user?.role?.name || t("Ciudadano")}
        </span>
      )}
      {customElement}
    </div>
  );

  const calculateLastName = (lastname: string): string => {
    if (!lastname) return "";

    let lastName = lastname.split(" ")[0];

    if (lastName.length <= 3) {
      // * check if the lastname is a middle name
      const split = lastname.split(" ");
      if (split.length > 1)
        lastName = `${lastname.split(" ")[0]} ${lastname.split(" ")[1]}`;
      else lastName = lastname[0];
    }

    return lastName;
  };

  const fullName = `${user?.name?.split(" ")[0]} ${
    calculateLastName(user?.lastname || "") || ""
  }`;

  return (
    <div className={`flex items-center ${showAvatar ? "gap-2" : ""}`}>
      <div className="relative">
        <UserAvatar user={user} size={size} />
        {icon && (
          <span
            className={`absolute h-6 w-6 flex items-center justify-center rounded-full bg-white dark:bg-zinc-800 shadow-sm ${iconClassName}`}
            style={{
              bottom: -7,
              right: -4,
            }}
          >
            {icon}
          </span>
        )}
      </div>
      <div className="flex w-max flex-col">
        {previewProfile ? (
          <UserTooltip user={user as Partial<IUser>} side="top">
            <div className="flex flex-col">
              <a
                href={`/profile/${user?.id}?vendorId=${storeSelectedVendor?.selectedVendor.id}`}
                target="_blank"
                rel="noreferrer"
                className="self-start text-left capitalize"
                style={{
                  wordBreak: "break-word",
                  fontSize,
                  fontWeight: 600,
                }}
              >
                {fullName?.toLowerCase()}
              </a>
              {showEmail && (
                <span
                  className="text-gray-800 dark:text-gray-300"
                  style={{ fontSize: fontSize || 11 }}
                >
                  {user?.email || "-"}
                </span>
              )}
              {showRole && (
                <span
                  className="text-xs capitalize text-gray-600 dark:text-gray-200"
                  style={{ fontSize: fontSize || 11 }}
                >
                  {user?.role?.name || "-"}
                </span>
              )}
              {customElement}
            </div>
          </UserTooltip>
        ) : (
          <Content />
        )}
      </div>
    </div>
  );
};

export default SimpUserBadge;
